import React from 'react';

interface ImagingTreatmentProps {
  imaging: {
    xray_views: string;
    xray_findings: string;
    mri_performed: boolean;
    mri_findings: string;
    ct_performed: boolean;
    ct_findings: string;
  };
  treatment: {
    type: string;
    conservative_plan: string;
    immobilization_type: string;
    immobilization_duration_weeks: number | null;
    surgical_indication: string;
    surgical_plan: string;
    weight_bearing_restriction: string;
    antibiotic_prophylaxis: boolean;
    tetanus_prophylaxis: boolean;
    next_appointment: string | null;
    observations: string;
  };
  complications: {
    neurovascular_deficit: boolean;
    infection: boolean;
    delayed_union: boolean;
    non_union: boolean;
    malunion: boolean;
    compartment_syndrome: boolean;
    thromboembolism_prophylaxis: boolean;
    other: string;
  };
  onFieldChange: (section: string, field: string, value: any) => void;
}

const TREATMENT_TYPE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'conservador', label: 'Conservador' },
  { value: 'quirúrgico', label: 'Quirúrgico' },
  { value: 'pendiente_especialista', label: 'Pendiente Especialista' },
];

const WEIGHT_BEARING_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'no apoyo', label: 'No Apoyo' },
  { value: 'apoyo parcial', label: 'Apoyo Parcial' },
  { value: 'apoyo según tolerancia', label: 'Apoyo según Tolerancia' },
  { value: 'apoyo completo', label: 'Apoyo Completo' },
];

const COMPLICATION_FIELDS: { key: string; label: string }[] = [
  { key: 'neurovascular_deficit', label: 'Déficit Neurovascular' },
  { key: 'infection', label: 'Infección' },
  { key: 'delayed_union', label: 'Retardo de Consolidación' },
  { key: 'non_union', label: 'Pseudoartrosis' },
  { key: 'malunion', label: 'Mal Unión' },
  { key: 'compartment_syndrome', label: 'Síndrome Compartimental' },
  { key: 'thromboembolism_prophylaxis', label: 'Profilaxis Tromboembólica' },
];

const ImagingTreatment: React.FC<ImagingTreatmentProps> = ({ imaging, treatment, complications, onFieldChange }) => {
  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-x-ray me-1"></i>
            Imágenes Diagnósticas
          </h5>
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Proyecciones Radiográficas</label>
                <input
                  type="text"
                  className="form-control"
                  value={imaging.xray_views}
                  onChange={e => onFieldChange('imaging', 'xray_views', e.target.value)}
                  placeholder="AP, Lateral, Oblicua, etc."
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Hallazgos Radiográficos</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={imaging.xray_findings}
                  onChange={e => onFieldChange('imaging', 'xray_findings', e.target.value)}
                  placeholder="Describa hallazgos en radiografías..."
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-3">
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="mri_performed"
                    checked={imaging.mri_performed}
                    onChange={e => onFieldChange('imaging', 'mri_performed', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="mri_performed">
                    Resonancia Magnética (RMN)
                  </label>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="ct_performed"
                    checked={imaging.ct_performed}
                    onChange={e => onFieldChange('imaging', 'ct_performed', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="ct_performed">
                    Tomografía (TAC)
                  </label>
                </div>
              </div>
            </div>
          </div>
          {imaging.mri_performed && (
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Hallazgos RMN</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={imaging.mri_findings}
                    onChange={e => onFieldChange('imaging', 'mri_findings', e.target.value)}
                    placeholder="Describa hallazgos de resonancia magnética..."
                  />
                </div>
              </div>
            </div>
          )}
          {imaging.ct_performed && (
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Hallazgos TAC</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={imaging.ct_findings}
                    onChange={e => onFieldChange('imaging', 'ct_findings', e.target.value)}
                    placeholder="Describa hallazgos de tomografía..."
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-syringe me-1"></i>
            Plan de Tratamiento
          </h5>
          <div className="row mt-3">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Tipo de Tratamiento</label>
                <select
                  className="form-select"
                  value={treatment.type}
                  onChange={e => onFieldChange('treatment', 'type', e.target.value)}
                >
                  {TREATMENT_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Restricción de Apoyo</label>
                <select
                  className="form-select"
                  value={treatment.weight_bearing_restriction}
                  onChange={e => onFieldChange('treatment', 'weight_bearing_restriction', e.target.value)}
                >
                  {WEIGHT_BEARING_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div className="mb-3">
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="antibiotic_prophylaxis"
                      checked={treatment.antibiotic_prophylaxis}
                      onChange={e => onFieldChange('treatment', 'antibiotic_prophylaxis', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="antibiotic_prophylaxis">
                      Profilaxis Antibiótica
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="tetanus_prophylaxis"
                      checked={treatment.tetanus_prophylaxis}
                      onChange={e => onFieldChange('treatment', 'tetanus_prophylaxis', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="tetanus_prophylaxis">
                      Profilaxis Antitetánica
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {treatment.type === 'conservador' && (
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Plan Conservador</label>
                  <input
                    type="text"
                    className="form-control"
                    value={treatment.conservative_plan}
                    onChange={e => onFieldChange('treatment', 'conservative_plan', e.target.value)}
                    placeholder="Describa plan conservador..."
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Tipo de Inmovilización</label>
                  <input
                    type="text"
                    className="form-control"
                    value={treatment.immobilization_type}
                    onChange={e => onFieldChange('treatment', 'immobilization_type', e.target.value)}
                    placeholder="Yeso, férula, etc."
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Duración Inmovilización (semanas)</label>
                  <input
                    type="number"
                    className="form-control"
                    min={0}
                    value={treatment.immobilization_duration_weeks ?? ''}
                    onChange={e => onFieldChange('treatment', 'immobilization_duration_weeks', e.target.value ? Number(e.target.value) : null)}
                    placeholder="Semanas"
                  />
                </div>
              </div>
            </div>
          )}

          {treatment.type === 'quirúrgico' && (
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Indicación Quirúrgica</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={treatment.surgical_indication}
                    onChange={e => onFieldChange('treatment', 'surgical_indication', e.target.value)}
                    placeholder="Justificación de la cirugía..."
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Plan Quirúrgico</label>
                  <input
                    type="text"
                    className="form-control"
                    value={treatment.surgical_plan}
                    onChange={e => onFieldChange('treatment', 'surgical_plan', e.target.value)}
                    placeholder="ORIF, enclavijado, artroplastía, etc."
                  />
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Próxima Cita</label>
                <input
                  type="date"
                  className="form-control"
                  value={treatment.next_appointment ?? ''}
                  onChange={e => onFieldChange('treatment', 'next_appointment', e.target.value || null)}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Observaciones</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={treatment.observations}
                  onChange={e => onFieldChange('treatment', 'observations', e.target.value)}
                  placeholder="Notas adicionales del plan de tratamiento..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-exclamation-triangle me-1"></i>
            Complicaciones y Riesgos
          </h5>
          <div className="row mt-3">
            {COMPLICATION_FIELDS.map(cf => (
              <div className="col-md-4" key={cf.key}>
                <div className="mb-2">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`complication_${cf.key}`}
                      checked={(complications as any)[cf.key]}
                      onChange={e => onFieldChange('complications', cf.key, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`complication_${cf.key}`}>
                      {cf.label}
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Otras Complicaciones</label>
                <input
                  type="text"
                  className="form-control"
                  value={complications.other}
                  onChange={e => onFieldChange('complications', 'other', e.target.value)}
                  placeholder="Especifique otras complicaciones..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImagingTreatment;
